/**
 * useActivityFeed Hook
 * 
 * Fetches and manages activity feed data across all portfolios.
 * Combines recent updates, comments, and status changes.
 * Isolates API calls from UI components per Rule 6.
 */

import { useState, useEffect, useCallback } from 'react';
import { authApi } from '../shared/utils/api';
import { logger } from '../shared/utils/logger';
import { Opportunity } from '../shared/types/opportunity';

export type ActivityType = 
  | 'status_change' 
  | 'price_update' 
  | 'comment' 
  | 'mention' 
  | 'dd_complete' 
  | 'dd_overdue'
  | 'created'
  | 'updated';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  subtitle: string;
  opportunityId: number;
  portfolioId: number;
  timestamp: Date;
  isRead: boolean;
}

interface UseActivityFeedResult {
  // All activity items
  activities: ActivityItem[];
  // Unread items
  unreadActivities: ActivityItem[];
  // Recent (read) items
  recentActivities: ActivityItem[];
  // Stats
  unreadCount: number;
  // Loading state
  loading: boolean;
  refreshing: boolean;
  refresh: () => Promise<void>;
  // Actions
  markAllRead: () => void;
  markRead: (activityId: string) => void;
  error: string | null;
}

export function useActivityFeed(): UseActivityFeedResult {
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchActivityFeed = useCallback(async (isRefresh = false) => {
    try {
      setError(null);
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch portfolios
      const portfolioResponse = await authApi.getPortfolios();
      const portfolios = portfolioResponse.portfolios || [];

      // Collect all opportunities with their updates
      const allActivities: ActivityItem[] = [];
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      for (const portfolio of portfolios) {
        try {
          const oppResponse = await authApi.getOpportunities(portfolio.id);
          const opportunities = oppResponse.opportunities || [];

          for (const opp of opportunities) {
            const updatedAt = opp.updated_at ? new Date(opp.updated_at) : null;
            const createdAt = opp.created_at ? new Date(opp.created_at) : null;
            const name = opp.name || opp.address || opp.street_address || 'Property';

            // Created recently (within 1 week)
            if (createdAt && createdAt > oneWeekAgo) {
              allActivities.push({
                id: `created-${opp.id}`,
                type: 'created',
                title: `${name} added`,
                subtitle: `Added to ${portfolio.name}`,
                opportunityId: opp.id,
                portfolioId: portfolio.id,
                timestamp: createdAt,
                isRead: readIds.has(`created-${opp.id}`),
              });
            }

            // Updated recently (within 1 day, not same as created)
            if (updatedAt && updatedAt > oneDayAgo && createdAt && 
                updatedAt.getTime() - createdAt.getTime() > 60000) {
              allActivities.push({
                id: `updated-${opp.id}-${updatedAt.getTime()}`,
                type: 'updated',
                title: `${name} updated`,
                subtitle: `Changes made to opportunity`,
                opportunityId: opp.id,
                portfolioId: portfolio.id,
                timestamp: updatedAt,
                isRead: readIds.has(`updated-${opp.id}-${updatedAt.getTime()}`),
              });
            }

            // Status-based activities
            if (opp.status === 'under_contract') {
              allActivities.push({
                id: `contract-${opp.id}`,
                type: 'status_change',
                title: `${name} under contract`,
                subtitle: 'Action may be required',
                opportunityId: opp.id,
                portfolioId: portfolio.id,
                timestamp: updatedAt || createdAt || now,
                isRead: readIds.has(`contract-${opp.id}`),
              });
            }

            if (opp.status === 'evaluating' && updatedAt && updatedAt > oneDayAgo) {
              allActivities.push({
                id: `eval-${opp.id}`,
                type: 'dd_overdue',
                title: `${name} needs review`,
                subtitle: 'Due diligence pending',
                opportunityId: opp.id,
                portfolioId: portfolio.id,
                timestamp: updatedAt,
                isRead: readIds.has(`eval-${opp.id}`),
              });
            }

            // Price update (if asking_price is set)
            if (opp.asking_price && updatedAt && updatedAt > oneWeekAgo) {
              const priceStr = opp.asking_price >= 1000000 
                ? `$${(opp.asking_price / 1000000).toFixed(1)}M`
                : `$${Math.round(opp.asking_price / 1000)}K`;
              
              allActivities.push({
                id: `price-${opp.id}`,
                type: 'price_update',
                title: `${name} price: ${priceStr}`,
                subtitle: 'Asking price recorded',
                opportunityId: opp.id,
                portfolioId: portfolio.id,
                timestamp: updatedAt,
                isRead: readIds.has(`price-${opp.id}`),
              });
            }
          }
        } catch (err) {
          logger.warn('Failed to fetch opportunities for activity', 'activity', {
            portfolioId: portfolio.id,
          });
        }
      }

      // Sort by timestamp descending
      allActivities.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

      // Dedupe by keeping only the most recent activity per opportunity type
      const seen = new Set<string>();
      const deduped = allActivities.filter(activity => {
        const key = `${activity.type}-${activity.opportunityId}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setActivities(deduped);

      logger.debug('Activity feed loaded', 'activity', {
        total: deduped.length,
        unread: deduped.filter(a => !a.isRead).length,
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load activity';
      logger.error('Failed to fetch activity feed', err, 'activity');
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [readIds]);

  useEffect(() => {
    fetchActivityFeed();
  }, [fetchActivityFeed]);

  const refresh = useCallback(() => fetchActivityFeed(true), [fetchActivityFeed]);

  // Mark all as read
  const markAllRead = useCallback(() => {
    setReadIds(new Set(activities.map(a => a.id)));
    setActivities(prev => prev.map(a => ({ ...a, isRead: true })));
  }, [activities]);

  // Mark single as read
  const markRead = useCallback((activityId: string) => {
    setReadIds(prev => new Set([...prev, activityId]));
    setActivities(prev => prev.map(a => 
      a.id === activityId ? { ...a, isRead: true } : a
    ));
  }, []);

  // Computed values
  const unreadActivities = activities.filter(a => !a.isRead);
  const recentActivities = activities.filter(a => a.isRead);
  const unreadCount = unreadActivities.length;

  return {
    activities,
    unreadActivities,
    recentActivities,
    unreadCount,
    loading,
    refreshing,
    refresh,
    markAllRead,
    markRead,
    error,
  };
}

/**
 * Get icon name for activity type
 */
export function getActivityIcon(type: ActivityType): string {
  switch (type) {
    case 'status_change': return 'flag-outline';
    case 'price_update': return 'cash-outline';
    case 'comment': return 'chatbubble-outline';
    case 'mention': return 'at';
    case 'dd_complete': return 'checkmark-circle-outline';
    case 'dd_overdue': return 'alert-circle';
    case 'created': return 'add-circle-outline';
    case 'updated': return 'create-outline';
    default: return 'notifications-outline';
  }
}

/**
 * Get color for activity type
 */
export function getActivityColor(type: ActivityType, isUnread: boolean): string {
  if (!isUnread) return '#9CA3AF'; // Gray for read items
  
  switch (type) {
    case 'dd_overdue': return '#EF4444'; // Red
    case 'status_change': return '#F59E0B'; // Amber
    case 'mention': return '#3B82F6'; // Blue
    case 'created': return '#10B981'; // Green
    default: return '#6B7280'; // Gray
  }
}

